import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Styles/RetailerSale.scss';
import { Link } from 'react-router-dom';
import './Styles/RetailerSalePayment.scss';

const RetailerSalePayment = () => {
  const [productCode, setProductCode] = useState('');
  const [message, setMessage] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isCompletingSale, setIsCompletingSale] = useState(false);

useEffect(() => {
  if (phoneNumber && purchaseDate && customerName && customerAddress) {
    fetchCustomerPurchases();
  }
}, [phoneNumber, purchaseDate, customerName, customerAddress]);

  const fetchCustomerPurchases = async () => {
    if (!phoneNumber || !purchaseDate) {
      return;
    }

    setIsFetching(true);
    try {
      const token = localStorage.getItem('retailerToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/auth/getPurchases`, {
        params: {
          customerPhoneNumber: phoneNumber,
          purchaseDate: purchaseDate
        },
        ...config
      });
      
      

      if (response.data.success && response.data.purchases.length > 0) {
        const purchase = response.data.purchases[0];
        
        // If customer details exist but haven't been set, populate them
        if (!customerName && purchase.customerName) {
          setCustomerName(purchase.customerName);
        }
        
        if (!customerAddress && purchase.customerAddress) {
          setCustomerAddress(purchase.customerAddress);
        }
        
        // Fetch details for each product in the purchase
        const productDetails = [];
        
        for (const productItem of purchase.products) {
          try {
            const productResponse = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/api/auth/codestatuscheck`,
              { 
                customerPhoneNumber: phoneNumber, 
                customerName: customerName || purchase.customerName, 
                customerAddress: customerAddress || purchase.customerAddress,
                purchaseDate, 
                productCode: productItem.productCode 
              },
              config
            );
            
            if (productResponse.data.success) {
              productDetails.push(productResponse.data.product);
            }
          } catch (error) {
            console.error(`Error fetching details for product ${productItem.productCode}:`, error);
          }
        }
        
        setSelectedProducts(productDetails);
        setMessage('Existing purchase loaded');
      } else {
        // No existing purchases, but don't clear products as user might be creating a new purchase
        setMessage('');
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
      const errorMsg = error.response?.data?.message || 'Failed to fetch purchases';
      if (errorMsg !== 'Customer not found.') {
        // Only show error if it's not just a new customer
        setMessage(errorMsg);
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const savedCustomerName = localStorage.getItem('customerName');
    const savedCustomerAddress = localStorage.getItem('customerAddress');
    const savedPhoneNumber = localStorage.getItem('phoneNumber');
    const savedPurchaseDate = localStorage.getItem('purchaseDate');
    
    if (savedCustomerName) setCustomerName(savedCustomerName);
    if (savedCustomerAddress) setCustomerAddress(savedCustomerAddress);
    if (savedPhoneNumber) setPhoneNumber(savedPhoneNumber);
    if (savedPurchaseDate) setPurchaseDate(savedPurchaseDate);
    
    // If you have both phone and date, fetch customer purchases
    if (savedPhoneNumber && savedPurchaseDate) {
      fetchCustomerPurchases();
    }
  }, []);
  
  // Update localStorage whenever these values change
  useEffect(() => {
    if (customerName) localStorage.setItem('customerName', customerName);
    if (customerAddress) localStorage.setItem('customerAddress', customerAddress);
    if (phoneNumber) localStorage.setItem('phoneNumber', phoneNumber);
    if (purchaseDate) localStorage.setItem('purchaseDate', purchaseDate);
  }, [customerName, customerAddress, phoneNumber, purchaseDate]);
  
  // Update clearCustomerInfo to also clear localStorage
  const clearCustomerInfo = () => {
    setCustomerName('');
    setCustomerAddress('');
    setPhoneNumber('');
    setPurchaseDate('');
    setSelectedProducts([]);
    setMessage('Customer information cleared');
    
    // Clear from localStorage
    localStorage.removeItem('customerName');
    localStorage.removeItem('customerAddress');
    localStorage.removeItem('phoneNumber');
    localStorage.removeItem('purchaseDate');
  };
  const handleProductCodeSubmit = async (e) => {
    e.preventDefault();  
    
    if (!productCode.trim()) {
      setMessage('Product code is required');
      return;
    }
    
    if (!customerName || !customerAddress) {
      setMessage('Customer name and address are required');
      return;
    }
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('retailerToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/codestatuscheck`,
        { 
          customerPhoneNumber: phoneNumber, 
          customerName, 
          customerAddress,
          purchaseDate, 
          productCode 
        },
        config
      );
  
      if (response.data.success) {
        // Check if product already exists in the list to avoid duplicates
        if (!selectedProducts.some(p => p.productCode === response.data.product.productCode)) {
          setSelectedProducts(prev => [...prev, response.data.product]);
          setMessage('Product added successfully');
        } else {
          setMessage('Product already added to the sale');
        }
      } else {
        setMessage(response.data.message || 'Failed to add product');
      }
  
      setProductCode('');
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setMessage(error.response?.data?.message || 'Error occurred');
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleRemoveProduct = async (productCodeToRemove) => {
    if (!phoneNumber || !purchaseDate) {
      // If no phone/date, just remove from local state
      setSelectedProducts(prev => prev.filter(p => p.productCode !== productCodeToRemove));
      return;
    }
    
    setIsRemoving(true);
    try {
      const token = localStorage.getItem('retailerToken');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/auth/deleteProduct`,
        { 
          data: {
            customerPhoneNumber: phoneNumber,
            purchaseDate,
            productCode: productCodeToRemove
          },
          ...config
        }
      );

      if (response.data.success) {
        // Update local state after successful deletion
        setSelectedProducts(prev => prev.filter(p => p.productCode !== productCodeToRemove));
        setMessage('Product removed successfully');
      }
    } catch (error) {
      console.error('Error removing product:', error);
      setMessage(error.response?.data?.message || 'Failed to remove product');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleFinalSale = async () => {
    if (selectedProducts.length === 0) {
      setMessage('No products selected for sale');
      return;
    }

    if (!customerName || !customerAddress) {
      setMessage('Customer name and address are required');
      return;
    }

    setIsCompletingSale(true);
    try {
      const token = localStorage.getItem('retailerToken');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const productCodes = selectedProducts.map(product => product.productCode);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/retailerSale`,
        {
          customerPhoneNumber: phoneNumber,
          customerName,
          customerAddress,
          purchaseDate,
          productCodes
        },
        config
      );

      if (response.data.success) {
        setMessage('Sale completed successfully');
        setSelectedProducts([]);
        setCustomerName('');
        setCustomerAddress('');
        setPhoneNumber('');
        setPurchaseDate('');
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'Sale failed');
    } finally {
      setIsCompletingSale(false);
    }
  };

  const totalPrice = selectedProducts.reduce((total, product) => {
    const price = product.price || 0;
    return total + price;
  }, 0);

  // Calculate loading state for specific product (for removal button)
  const isProductRemoving = (productCode) => {
    return isRemoving && selectedProducts.some(p => p.productCode === productCode);
  };

  return (
    <div className='RetailerMain'>
      <div className='RetailerXX'>
        <nav className="retailer-navbar">
          <ul>
            <li><Link to={`/Retailer-Home`}>Retailer Home</Link></li>
            <li><Link to={`/Retailer-Home/Add`}>Add Product</Link></li>
            <li><Link to={`/Retailer-Home/Verification`}>Product Verification</Link></li>
            <li><Link to="/Retailer-Home/SalesDashboard">Sales Dashboard</Link></li>
          </ul>
        </nav>
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-md-8">
            <div className='Retailer-Sale-Payment'>
              <h2>Retailer Sales</h2>
              <form className='form-Sale-Payment' onSubmit={handleProductCodeSubmit}>
                <div className="customer-details mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5>Customer Information</h5>
                    <button 
                      type="button" 
                      className="btn btn-danger btn-sm"
                      onClick={clearCustomerInfo}
                    >
                      Clear Customer Info
                    </button>
                  </div>
                  <input 
                    type="text" 
                    placeholder="Customer Name" 
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="form-control mb-2"
                    required
                  />
                  <input 
                    type="text" 
                    placeholder="Customer Address" 
                    value={customerAddress}
                    onChange={(e) => setCustomerAddress(e.target.value)}
                    className="form-control mb-2"
                    required
                  />
                  <div className="d-flex gap-2 mb-2">
                    <input 
                      type="text" 
                      placeholder="Customer Phone Number" 
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="form-control"
                      required
                    />
                    <input 
                      type="date" 
                      value={purchaseDate}
                      onChange={(e) => setPurchaseDate(e.target.value)}
                      className="form-control"
                      required
                    />
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={fetchCustomerPurchases}
                      disabled={isFetching || !phoneNumber || !purchaseDate}
                    >
                      {isFetching ? 'Fetching...' : 'Fetch'}
                    </button>
                  </div>
                </div>
                <div className="form-field">
                  <label htmlFor="productCode">Product Code:</label>
                  <div className="d-flex gap-2">
                    <input
                      id="productCode"
                      type="text"
                      value={productCode}
                      onChange={(e) => setProductCode(e.target.value)}
                      placeholder="Enter product code"
                      className="form-control"
                    />
                    <button 
                      type="submit" 
                      className="btn btn-primary" 
                      disabled={isLoading || !productCode.trim()}
                    >
                      {isLoading ? 'Adding...' : 'Add Product'}
                    </button>
                  </div>
                </div>
              </form>

              {message && (
                <div className={`alert ${message.includes('success') || message.includes('cleared') ? 'alert-success' : 'alert-danger'} mt-3`}>
                  {message}
                </div>
              )}

              <div className="selected-products mt-3">
                <h4>Selected Products</h4>
                {selectedProducts.length === 0 ? (
                  <p className="text-muted">No products selected yet</p>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product Name</th>
                        <th>Size</th>
                        <th>Price</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProducts.map((product) => (
                        <tr key={product.productCode}>
                          <td>{product.productName}</td>
                          <td>{product.size}</td>
                          <td>₹{product.price}</td>
                          <td>
                            <button 
                              className="btn btn-sm btn-danger"
                              onClick={() => handleRemoveProduct(product.productCode)}
                              disabled={isProductRemoving(product.productCode)}
                            >
                              {isProductRemoving(product.productCode) ? 'Removing...' : 'Remove'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                
                {selectedProducts.length > 0 && (
                  <button 
                    className="btn btn-success"
                    onClick={handleFinalSale}
                    disabled={isCompletingSale}
                  >
                    {isCompletingSale ? 'Processing Sale...' : 'Complete Sale'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="payment-card card">
              <div className="card-header">
                <h3>Payment Details</h3>
              </div>
              <div className="card-body">
                {/* Customer Details Section */}
                <div className="customer-info mb-3">
                  <h5>Customer Information</h5>
                  <p><strong>Name:</strong> {customerName || 'Not provided'}</p>
                  <p><strong>Address:</strong> {customerAddress || 'Not provided'}</p>
                  <p><strong>Phone Number:</strong> {phoneNumber || 'Not provided'}</p>
                  <p><strong>Purchase Date:</strong> {purchaseDate || 'Not selected'}</p>
                </div>

                {/* Selected Products Section */}
                <div className="selected-products-summary mb-3">
                  <h5>Products</h5>
                  {selectedProducts.length === 0 ? (
                    <p>No products selected</p>
                  ) : (
                    <ul className="list-group">
                      {selectedProducts.map((product) => (
                        <li 
                          key={product.productCode} 
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          {product.productName}
                          <span className="badge bg-primary rounded-pill">{product.size}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="price-summary">
                  <h5>Price Summary</h5>
                  <div className="d-flex justify-content-between">
                    <span>Total Products:</span>
                    <span>{selectedProducts.length}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <strong>Total Price:</strong>
                    <strong>₹{totalPrice.toLocaleString()}</strong>
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <div className="payment-methods">
                  <h6>Payment Methods</h6>
                  <div className="d-flex justify-content-between">
                    <button className="btn btn-outline-primary">Cash</button>
                    <button className="btn btn-outline-secondary">Card</button>
                    <button className="btn btn-outline-success">UPI</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailerSalePayment;